import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { apiGet } from '../../../utils/api';

import ClientOverviewPanel from './client-manager/ClientOverviewPanel';
import ClientUsersPanel from './client-manager/ClientUsersPanel';
import ClientRolesPanel from './client-manager/ClientRolesPanel';
import ClientHierarchyPanel from './client-manager/ClientHierarchyPanel';
import ClientFeaturesPanel from './client-manager/ClientFeaturesPanel';
import ClientBillingPanel from './client-manager/ClientBillingPanel';

const ClientManagerDashboard = () => {
    const { theme } = useTheme();
    const { currentUser, switchClient } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');

    // Multi-client support
    const [myClients, setMyClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [loadingClients, setLoadingClients] = useState(true);

    // Guard: only client managers can access
    if (!currentUser?.isClientManager) {
        return <Navigate to="/dashboard" replace />;
    }

    useEffect(() => {
        const load = async () => {
            try {
                const res = await apiGet('/client_manager/my_clients');
                if (res.ok) {
                    const data = await res.json();
                    setMyClients(data.clients || []);
                    if (data.clients?.length > 0 && !selectedClientId) {
                        setSelectedClientId(data.clients[0].client_id);
                    }
                }
            } catch {}
            setLoadingClients(false);
        };
        load();
    }, []);

    const dark = theme === 'dark';
    const isMultiClient = myClients.length > 1;
    const selectedClient = myClients.find((c) => c.client_id === selectedClientId);

    const tabClass = (tab) =>
        `py-2 px-4 font-medium font-rasa whitespace-nowrap ${
            activeTab === tab
                ? 'text-blue-500 border-b-2 border-blue-500'
                : `${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
        }`;

    if (loadingClients) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
        );
    }

    return (
        <div className={`h-full overflow-y-auto p-4 max-w-screen-2xl mx-auto ${dark ? 'text-white' : 'text-gray-900'}`}>
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-dm-serif">Client Management</h1>
                    <p className={`text-sm font-rasa mt-1 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {selectedClient ? selectedClient.company_name : 'Manage your organization'}
                    </p>
                </div>
                {isMultiClient && (
                    <select
                        value={selectedClientId || ''}
                        onChange={(e) => {
                            const newId = Number(e.target.value);
                            setSelectedClientId(newId);
                            switchClient(newId);
                        }}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                            dark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                        {myClients.map((c) => (
                            <option key={c.client_id} value={c.client_id}>{c.company_name}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Tab bar */}
            <div className={`border-b ${dark ? 'border-gray-700' : 'border-gray-200'} flex gap-0 overflow-x-auto`}>
                <button onClick={() => setActiveTab('overview')} className={tabClass('overview')}>Overview</button>
                <button onClick={() => setActiveTab('users')} className={tabClass('users')}>Users</button>
                <button onClick={() => setActiveTab('roles')} className={tabClass('roles')}>Roles</button>
                <button onClick={() => setActiveTab('hierarchy')} className={tabClass('hierarchy')}>Hierarchy</button>
                <button onClick={() => setActiveTab('features')} className={tabClass('features')}>Features</button>
                <button onClick={() => setActiveTab('billing')} className={tabClass('billing')}>Billing</button>
            </div>

            {/* Tab content — pass clientId so panels scope to the right client */}
            {activeTab === 'overview' && <ClientOverviewPanel clientId={selectedClientId} />}
            {activeTab === 'users' && <ClientUsersPanel clientId={selectedClientId} />}
            {activeTab === 'roles' && <ClientRolesPanel clientId={selectedClientId} />}
            {activeTab === 'hierarchy' && <ClientHierarchyPanel clientId={selectedClientId} />}
            {activeTab === 'features' && <ClientFeaturesPanel clientId={selectedClientId} />}
            {activeTab === 'billing' && <ClientBillingPanel clientId={selectedClientId} />}
        </div>
        </div>
    );
};

export default ClientManagerDashboard;
